/**
 * Input tax credit.
 *
 * Output tax is a liability the moment you invoice. Input tax is not an asset
 * the moment you are billed — it becomes claimable only when every condition in
 * section 16 is met, it is not blocked by section 17(5), and it survives the
 * 180-day payment rule. Treating input tax as automatically recoverable is the
 * single most common error in Indian bookkeeping software, and it overstates
 * both profit and the balance sheet.
 *
 * This module answers one question per bill: how much of this tax can we
 * actually claim, when, and why not.
 */

import { round2 } from "../sales/tax";
import type { ExpenseNature, ItcEligibility, PurchaseDocument, PurchaseLine } from "./types";

/**
 * Section 17(5) blocked credits. Each entry names the clause so a reviewer can
 * check the call rather than trust it.
 */
export const BLOCKED_NATURES: Partial<Record<ExpenseNature, { clause: string; reason: string; exception?: string }>> = {
  "motor-vehicle": {
    clause: "17(5)(a)",
    reason: "Motor vehicles for transport of persons with seating capacity up to 13.",
    exception: "Available when used for further supply of vehicles, passenger transport, or driving instruction."
  },
  "food-and-beverage": {
    clause: "17(5)(b)(i)",
    reason: "Food and beverages, outdoor catering, beauty treatment, health services.",
    exception: "Available where the inward supply is used to make an outward taxable supply of the same category, or where provision to employees is obligatory under law."
  },
  "club-membership": { clause: "17(5)(b)(ii)", reason: "Membership of a club, health and fitness centre." },
  "insurance-life-health": {
    clause: "17(5)(b)(i)",
    reason: "Life and health insurance.",
    exception: "Available where obligatory for an employer to provide under any law."
  },
  "works-contract": {
    clause: "17(5)(c)",
    reason: "Works contract services for construction of immovable property.",
    exception: "Available where it is an input service for further supply of works contract service, or for plant and machinery."
  },
  "construction-immovable": {
    clause: "17(5)(d)",
    reason: "Goods or services for construction of immovable property on own account.",
    exception: "Available for plant and machinery."
  },
  csr: { clause: "17(5)(fa)", reason: "Goods or services used for CSR obligations under section 135 of the Companies Act." },
  "employee-welfare": {
    clause: "17(5)(g)",
    reason: "Goods or services used for personal consumption.",
    exception: "Available where the expense is a business necessity rather than a personal benefit — judgement required."
  }
};

/** Supplies where the recipient pays the tax under section 9(3). */
export const RCM_NATURES: Partial<Record<ExpenseNature, { notification: string; note: string }>> = {
  "transport-gta": { notification: "9(3) — GTA", note: "Reverse charge unless the GTA has opted to pay at 12% under forward charge." },
  "legal-services": { notification: "9(3) — Advocate", note: "Legal services from an individual advocate or firm of advocates to a business entity." },
  "director-services": { notification: "9(3) — Director", note: "Services by a director to the company, other than in the capacity of employee." },
  sponsorship: { notification: "9(3) — Sponsorship", note: "Sponsorship services to a body corporate or partnership firm." },
  "import-of-services": { notification: "9(3) — Import", note: "Services imported from outside India by a person in the taxable territory." }
};

export interface ItcAssessment {
  eligibility: ItcEligibility;
  /** Tax that may be claimed now. */
  claimable: number;
  /** Tax that is permanently a cost — added to the expense or asset. */
  blocked: number;
  /** Tax withheld pending a condition, claimable later. */
  deferred: number;
  reasons: string[];
  /** Clause references, for the reviewer and the audit file. */
  clauses: string[];
  rcmApplicable: boolean;
  rcmNote?: string;
}

export interface ItcContext {
  /** The bill appears in GSTR-2B for the period. Section 16(2)(aa). */
  reflectedIn2B?: boolean;
  /** Goods or services actually received. Section 16(2)(b). */
  received?: boolean;
  /** Proportion used for business, where the supply is used partly privately. */
  businessUsePercent?: number;
  /** Entity makes exempt supplies too, so rule 42 reversal applies. */
  exemptTurnoverRatio?: number;
  /** Date the bill was paid, for the 180-day rule. */
  paidOn?: string;
  /** Date the assessment is made. */
  asAt?: string;
  /** Whether the entity is registered under composition — no ITC at all. */
  compositionScheme?: boolean;
  /** Block the claim until the invoice appears in 2B. */
  blockWithout2B?: boolean;
}

/**
 * Assesses a whole bill. Returns the split between claimable, blocked and
 * deferred tax so the posting can put each part in the right place: claimable
 * to the input credit ledger, blocked to expense, deferred to a holding
 * account that clears when the condition is met.
 */
export const assessItc = (
  document: Pick<PurchaseDocument, "lines" | "rcm" | "date" | "supplyType">,
  totalTax: number,
  context: ItcContext = {}
): ItcAssessment => {
  const reasons: string[] = [];
  const clauses: string[] = [];

  const rcmNature = document.lines
    .map((line) => line.expenseNature)
    .find((nature) => nature && RCM_NATURES[nature]);
  const rcmApplicable = Boolean(document.rcm || rcmNature);
  const rcmNote = rcmNature ? RCM_NATURES[rcmNature]!.note : undefined;

  // A composition dealer cannot claim any input credit. The tax is a cost.
  if (context.compositionScheme) {
    return {
      eligibility: "blocked-17-5",
      claimable: 0,
      blocked: round2(totalTax),
      deferred: 0,
      reasons: ["Entity is registered under the composition scheme, which does not permit input tax credit."],
      clauses: ["10(4)"],
      rcmApplicable,
      rcmNote
    };
  }

  // Blocked credits are decided line by line, so a bill with one blocked line
  // does not lose the credit on the rest.
  let blocked = 0;
  let eligibleBase = 0;
  const lineTaxShare = splitTaxByLine(document.lines, totalTax);

  for (const line of document.lines) {
    const share = lineTaxShare.get(line.id) ?? 0;
    const nature = line.expenseNature;
    const block = nature ? BLOCKED_NATURES[nature] : undefined;
    if (block) {
      blocked = round2(blocked + share);
      reasons.push(`"${line.description}" — ${block.reason}`);
      if (!clauses.includes(block.clause)) clauses.push(block.clause);
    } else {
      eligibleBase = round2(eligibleBase + share);
    }
  }

  let claimable = eligibleBase;
  let deferred = 0;

  // Section 17(1)/(2): apportion where the supply is partly non-business or
  // used for exempt supplies.
  if (context.businessUsePercent !== undefined && context.businessUsePercent < 100) {
    const personal = round2((claimable * (100 - context.businessUsePercent)) / 100);
    blocked = round2(blocked + personal);
    claimable = round2(claimable - personal);
    reasons.push(`${100 - context.businessUsePercent}% non-business use apportioned out under section 17(1).`);
    if (!clauses.includes("17(1)")) clauses.push("17(1)");
  }
  if (context.exemptTurnoverRatio && context.exemptTurnoverRatio > 0) {
    const reversal = round2(claimable * context.exemptTurnoverRatio);
    blocked = round2(blocked + reversal);
    claimable = round2(claimable - reversal);
    reasons.push(`Proportionate reversal of ${(context.exemptTurnoverRatio * 100).toFixed(2)}% for exempt supplies under rule 42.`);
    if (!clauses.includes("Rule 42")) clauses.push("Rule 42");
  }

  // Section 16(2)(b): credit requires actual receipt of the goods or services.
  if (context.received === false) {
    deferred = round2(deferred + claimable);
    claimable = 0;
    reasons.push("Goods or services not yet received — credit deferred under section 16(2)(b).");
    if (!clauses.includes("16(2)(b)")) clauses.push("16(2)(b)");
  }

  // Section 16(2)(aa): the invoice must be furnished by the supplier and appear
  // in GSTR-2B. This is the condition that most often defers a credit.
  if (claimable > 0 && context.reflectedIn2B === false) {
    if (context.blockWithout2B) {
      deferred = round2(deferred + claimable);
      claimable = 0;
    }
    reasons.push("Invoice not reflected in GSTR-2B — credit is not available until the supplier reports it, under section 16(2)(aa).");
    if (!clauses.includes("16(2)(aa)")) clauses.push("16(2)(aa)");
  }

  // Second proviso to 16(2): pay the supplier within 180 days or reverse.
  const asAt = context.asAt ?? new Date().toISOString().slice(0, 10);
  const daysUnpaid = context.paidOn
    ? 0
    : Math.floor((new Date(asAt).getTime() - new Date(document.date).getTime()) / 86_400_000);
  const reversedFor180 = !context.paidOn && daysUnpaid > 180 && claimable > 0;
  if (reversedFor180) {
    reasons.push(`Unpaid for ${daysUnpaid} days. Credit must be reversed under the second proviso to section 16(2) and may be reclaimed on payment.`);
    if (!clauses.includes("16(2) second proviso")) clauses.push("16(2) second proviso");
  }

  // Section 16(4): credit lapses after 30 November following the financial year.
  const deadline = itcDeadline(document.date);
  if (asAt > deadline && claimable > 0) {
    blocked = round2(blocked + claimable);
    claimable = 0;
    reasons.push(`The time limit under section 16(4) expired on ${deadline}. This credit can no longer be claimed.`);
    if (!clauses.includes("16(4)")) clauses.push("16(4)");
  }

  const eligibility: ItcEligibility = reversedFor180
    ? "reversed-180-day"
    : deferred > 0 && claimable === 0
      ? "pending-2b"
      : blocked > 0 && claimable === 0
        ? "blocked-17-5"
        : claimable > 0
          ? "eligible"
          : "not-applicable";

  if (!reasons.length) reasons.push("All section 16 conditions met and no section 17(5) block applies.");

  return {
    eligibility,
    claimable: round2(claimable),
    blocked: round2(blocked),
    deferred: round2(deferred),
    reasons,
    clauses,
    rcmApplicable,
    rcmNote
  };
};

/** Splits document tax across lines in proportion to their taxable value. */
const splitTaxByLine = (lines: PurchaseLine[], totalTax: number): Map<string, number> => {
  const values = lines.map((line) => {
    const gross = line.quantity * line.unitPrice;
    const discount = (line.discountPercent ? (gross * line.discountPercent) / 100 : 0) + (line.discountAmount ?? 0);
    return Math.max(0, gross - discount);
  });
  const total = values.reduce((sum, value) => sum + value, 0);
  const result = new Map<string, number>();
  if (total <= 0) {
    lines.forEach((line) => result.set(line.id, 0));
    return result;
  }
  let allocated = 0;
  lines.forEach((line, index) => {
    const share = index === lines.length - 1 ? round2(totalTax - allocated) : round2((totalTax * values[index]) / total);
    allocated = round2(allocated + share);
    result.set(line.id, share);
  });
  return result;
};

/**
 * The section 16(4) deadline: 30 November following the end of the financial
 * year in which the invoice was issued, or the date of the annual return,
 * whichever is earlier.
 */
export const itcDeadline = (invoiceDate: string): string => {
  const parsed = new Date(invoiceDate);
  const year = parsed.getUTCFullYear();
  const startYear = parsed.getUTCMonth() >= 3 ? year : year - 1;
  return `${startYear + 1}-11-30`;
};

/** Days remaining before the 180-day payment rule bites. */
export const daysTo180 = (billDate: string, asAt: string): number =>
  180 - Math.floor((new Date(asAt).getTime() - new Date(billDate).getTime()) / 86_400_000);

/** Bills whose credit is about to reverse — the 180-day watchlist. */
export interface ReversalWatchItem {
  billId: string;
  billNumber: string;
  partnerName: string;
  billDate: string;
  outstanding: number;
  itcAtRisk: number;
  daysRemaining: number;
  status: "reversed" | "critical" | "watch" | "safe";
}

export const buildReversalWatchlist = (
  bills: Array<{ id: string; number: string; partnerName: string; date: string; outstanding: number; inputTax: number; paidOn?: string }>,
  asAt: string,
  warnWithinDays = 30
): ReversalWatchItem[] =>
  bills
    .filter((bill) => !bill.paidOn && bill.outstanding > 0)
    .map((bill) => {
      const daysRemaining = daysTo180(bill.date, asAt);
      return {
        billId: bill.id,
        billNumber: bill.number,
        partnerName: bill.partnerName,
        billDate: bill.date,
        outstanding: round2(bill.outstanding),
        itcAtRisk: round2(bill.inputTax),
        daysRemaining,
        status: daysRemaining < 0 ? "reversed" : daysRemaining <= warnWithinDays ? "critical" : daysRemaining <= 60 ? "watch" : "safe"
      } as ReversalWatchItem;
    })
    .filter((item) => item.status !== "safe")
    .sort((left, right) => left.daysRemaining - right.daysRemaining);
